import React, { useState, useEffect } from 'react';

type Item = {
  src: string;
  alt: string;
  title: string;
  desc: string;
};

const items: Item[] = [
  {
    src: '/img/lms/Activity-LMS-Documentation.jpg',
    alt: 'Activity LMS Documentation',
    title: 'Activity',
    desc: 'Menampilkan berbagai jenis aktivitas pembelajaran yang tersedia tugas, forum diskusi, kuis, materi bacaan, video pembelajaran, hingga sertifikat.',
  },
  {
    src: '/img/lms/Course-LMS-Documentation.jpg',
    alt: 'Course LMS Documentation',
    title: 'Course',
    desc: 'Menampilkan daftar mata pelajaran yang tersedia dan dapat diakses oleh siswa. Setiap kotak merepresentasikan satu kursus lengkap dengan nama dan status progres.',
  },
  {
    src: '/img/lms/Detail-Course-LMS-Documentation.jpg',
    alt: 'Detail Course LMS Documentation',
    title: 'Detail Course',
    desc: 'Menampilkan struktur dan konten pembelajaran dari sebuah mata pelajaran yang terdiri dari berbagai jenis aktivitas atau sumber belajar.',
  },
  {
    src: '/img/lms/Grading-LMS-Documentation.jpg',
    alt: 'Grading LMS Documentation',
    title: 'Grading',
    desc: 'Menampilkan ringkasan penilaian untuk setiap tugas, kuis, dan aktivitas lainnya yang telah diselesaikan oleh peserta.',
  },
  {
    src: '/img/lms/Roles-LMS-Documentation.jpg',
    alt: 'Roles LMS Documentation',
    title: 'Roles',
    desc: 'Menentukan hak akses dan tanggung jawab setiap pengguna, seperti admin, pengajar, dan siswa dalam mengelola dan menggunakan sistem.',
  },
  {
    src: '/img/lms/Dashboard-LMS-Documentation.jpg',
    alt: 'Dashboard LMS Documentation',
    title: 'Dashboard',
    desc: 'Menampilkan pusat informasi dan pengelolaan pembelajaran digital berisi pengumuman, jadwal mendatang, dan timeline aktivitas pembelajaran',
  },
  {
    src: '/img/lms/Anounce-LMS-Documentation.jpg',
    alt: 'Anounce & Reminder LMS Documentation',
    title: 'Announce & Reminder',
    desc: 'Menampilkan informasi penting dan pengingat tugas, deadline, atau kegiatan agar pengguna selalu update dan tepat waktu',
  },
  {
    src: '/img/lms/Detail-Report-LMS-Documentation.jpg',
    alt: 'Detail Report of User LMS Documentation',
    title: 'Detail Report of User',
    desc: 'Memuat informasi lengkap tentang pengguna dalam kursus tertentu, mulai dari identitas, daftar kursus yang diikuti, aktivitas terakhir, hingga akses terhadap laporan nilai.',
  },
  {
    src: '/img/lms/Date-LMS-Documentation.jpg',
    alt: 'Date LMS Documentation',
    title: 'Date Availability',
    desc: 'Pengaturan ketersediaan dan batas waktu  suatu aktivitas para pengguna',
  },
  {
    src: '/img/lms/Bulk-LMS-Documentation.jpg',
    alt: 'Bulk LMS Documentation',
    title: 'Bulk Upload',
    desc: 'Menampilkan fitur unggahan banyak file, materi pembelajaran, atau data peserta secara efisien',
  },
];

const LmsDocumentation: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [screenSize, setScreenSize] = useState<'small' | 'medium' | 'large'>('large');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('small');
      } else if (width >= 768 && width < 1024) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => {
      if (prev === 0) {
        return items.length - getVisibleCount();
      } else {
        return prev - 1;
      }
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      if (prev + getVisibleCount() >= items.length) {
        return 0;
      } else {
        return prev + 1;
      }
    });
  };

  const getVisibleCount = () => {
    switch (screenSize) {
      case 'small': return 1;
      case 'medium': return 2;
      case 'large': return 3;
      default: return 3;
    }
  };

  const openImagePopup = (src: string) => {
    setSelectedImage(src);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  return (
    <section className="w-full px-4 py-8">
      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" 
          onClick={closeImagePopup}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={selectedImage}
              alt="Enlarged view"
              className="max-w-full max-h-[80vh] object-contain"
            />
            
            <button
              onClick={closeImagePopup}
              className="absolute -top-5 -right-5 w-12 h-12 flex items-center justify-center font-bold shadow-lg bg-gray-300 rounded-full text-gray-700 hover:bg-gray-400 hover:text-black text-3xl leading-none"
            >
              <span className="relative -top-[3px]">&times;</span>
            </button>
          </div>
        </div>
      )}

      {screenSize === 'small' ? (
        <div className="max-w-6xl mx-auto relative flex justify-center">
          <div className="overflow-hidden bg-white rounded-md shadow-[4px_4px_10px_0px_rgba(0,128,128,0.5)] p-1 w-full h-[480px] flex flex-col items-center">
            <div 
              className="cursor-pointer" 
              onClick={() => {
                const src = items[currentIndex]?.src;
                if (src) {
                  openImagePopup(src);
                }
              }}
            >
              <img
                src={items[currentIndex]?.src}
                alt={items[currentIndex]?.alt}
                className="h-72 object-contain transition-all duration-300"
              />
            </div>
            <p className='text-white bg-teal-700 font-semibold mt-6 rounded-md p-1 w-44 text-center'>{items[currentIndex]?.title}</p>
            <p className='m-4 text-justify'>{items[currentIndex]?.desc}</p>
            <button
              onClick={handleNext}
              className={`absolute w-10 h-10 -right-4 top-1/2 -translate-y-1/2 z-10 shadow p-2 rounded-full flex items-center justify-center ${currentIndex === items.length - 1 ? 'hidden' : ''}`}
            >
              <img
                src="/img/lms/right-arrow.png"
                alt="Right"
                className="absolute inset-0 m-auto"
              />
            </button>
            <button
              onClick={handlePrev}
              className={`absolute w-10 h-10 -left-4 top-1/2 -translate-y-1/2 z-10 shadow p-2 rounded-full flex items-center justify-center ${currentIndex === 0 ? 'hidden' : ''}`}
            >
              <img
                src="/img/lms/left-arrow.png"
                alt="Left"
                className="absolute inset-0 m-auto"
              />
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 relative -mt-6">
          {currentIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 shadow p-2 rounded-full flex items-center justify-center"
            >
              <img
                src="/img/lms/left-arrow.png"
                alt="Left"
                className="absolute inset-0 m-auto"
              />
            </button>
          )}

          {items.slice(currentIndex, currentIndex + getVisibleCount()).map((item, index) => (
            <div key={index} className="flex-1 overflow-hidden bg-white rounded-lg shadow-[4px_4px_10px_0px_rgba(0,128,128,0.5)] p-4 w-[350px] h-[440px] flex flex-col items-center">
              <div 
                className="cursor-pointer w-full " 
                onClick={() => openImagePopup(item.src)}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-[13rem] object-cover object-top rounded-md transition-all duration-300 hover:scale-[1.02]"
                />
              </div>
              <p className='text-white bg-teal-700 font-semibold mt-6 rounded-md p-1 w-44 text-center'>{item.title}</p>
              <p className='m-6 text-justify'>{item.desc}</p>
            </div>
          ))}

          {currentIndex + getVisibleCount() < items.length && (
            <button
              onClick={handleNext}
              className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 shadow p-2 rounded-full flex items-center justify-center"
            >
              <img
                src="/img/lms/right-arrow.png"
                alt="Right"
                className="absolute inset-0 m-auto"
              />
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default LmsDocumentation;